import { HStack, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { LuSettings2 } from "react-icons/lu";

import {
  BiddingStrategy,
  OptimizationRule,
  OptimizationTarget,
} from "../types/optimization-rule";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import IconCircle from "./icon-circle";

function formatOptimizationTarget(target: OptimizationTarget): string {
  switch (target) {
    case OptimizationTarget.FOP:
      return "First-Order Profit";
    case OptimizationTarget.CLTV:
      return "Customer Lifetime Value (LTV)";
    case OptimizationTarget.CLTP:
      return "Customer Lifetime Profit (LTP)";
    case OptimizationTarget.CP:
      return "Conversion Propensity";
    case OptimizationTarget.Custom:
      return "Custom";
    default:
      return target;
  }
}

function formatCutoffPercentage(cutoff: number | null): string {
  if (!cutoff) return "";
  return `${(cutoff * 100).toFixed(0)}%`;
}

function formatBiddingStrategy(strategy: BiddingStrategy): string {
  switch (strategy) {
    case BiddingStrategy.Value:
      return "Value-Based";
    case BiddingStrategy.LowestCPA:
      return "Lowest CPA";
    default:
      return strategy;
  }
}

interface OptimizationRuleDetailsDialogProps {
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  rule: OptimizationRule;
}

export function OptimizationRuleDetailsDialog({
  isOpen,
  onOpenChange,
  rule,
}: OptimizationRuleDetailsDialogProps) {
  return (
    <DialogRoot open={isOpen} onOpenChange={onOpenChange} size="md">
      <DialogContent bg="bg.muted">
        <DialogCloseTrigger colorPalette="gray" />
        <DialogHeader bg="bg" borderTopRadius="md">
          <HStack>
            <IconCircle Icon={LuSettings2} />
            <DialogTitle>{rule.name}</DialogTitle>
          </HStack>
        </DialogHeader>
        <DialogBody bg="bg" borderBottomRadius="md" borderBottomWidth="1" p="6">
          <Stack gap="6">
            <Stack>
              <Text fontWeight="medium" color="fg.muted">
                Optimization target
              </Text>
              <Text fontSize="lg">{formatOptimizationTarget(rule.target)}</Text>
            </Stack>

            <Stack>
              <Text fontWeight="medium" color="fg.muted">
                Bidding strategy
              </Text>
              <Text fontSize="lg">
                {formatBiddingStrategy(rule["bidding-strategy"])}
                {rule["custom-value-cutoff"] &&
                  `, ${formatCutoffPercentage(rule["custom-value-cutoff"])} cutoff`}
                {rule["custom-cpa-cutoff"] &&
                  `, ${formatCutoffPercentage(rule["custom-cpa-cutoff"])} cutoff`}
              </Text>
            </Stack>

            <Stack>
              <Text fontWeight="medium" color="fg.muted">
                Timeframe
              </Text>
              <Text fontSize="lg">{rule.period} days</Text>
            </Stack>

            {rule.target === OptimizationTarget.Custom && (
              <Stack>
                <Text fontWeight="medium" color="fg.muted">
                  Custom Weights
                </Text>
                <Stack gap="2">
                  {rule["custom-fop-weight"] !== null && (
                    <HStack justify="space-between">
                      <Text>First-Order Profit</Text>
                      <Text>
                        {Math.round(rule["custom-fop-weight"] * 100)}%
                      </Text>
                    </HStack>
                  )}
                  {rule["custom-cltv-weight"] !== null && (
                    <HStack justify="space-between">
                      <Text>Future Value</Text>
                      <Text>
                        {Math.round(rule["custom-cltv-weight"] * 100)}%
                      </Text>
                    </HStack>
                  )}
                  {rule["custom-cltp-weight"] !== null && (
                    <HStack justify="space-between">
                      <Text>Future Profit</Text>
                      <Text>
                        {Math.round(rule["custom-cltp-weight"] * 100)}%
                      </Text>
                    </HStack>
                  )}
                </Stack>
              </Stack>
            )}
          </Stack>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
